import { HttpClient, HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AppConst, AppConsts } from '../AppConsts';
import { ApiResponse } from '../../interfaces/api';
import { RefreshTokenResult } from '../../interfaces/auth';
import { LocalStorageKey } from '../constants';
import { resolveLoginRoute } from '../utils/login-route';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const http = inject(HttpClient);
  const router = inject(Router);
  const token = localStorage.getItem(LocalStorageKey.token);

  return next(withAuthHeader(req, token)).pipe(
    catchError((error) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        !req.url.includes('RefreshToken')
      ) {
        return refreshAndRetry(http, router, req, next);
      }
      return throwError(() => error);
    })
  );
};

function withAuthHeader(req: HttpRequest<unknown>, token: string | null) {
  if (!token) return req;
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}
// function withAuthHeader(
//   req: HttpRequest<unknown>,
//   token: string | null
// ): HttpRequest<unknown> {
//   const headers: Record<string, string> = {
//     'ngrok-skip-browser-warning': 'true',
//   };

//   if (token) {
//     headers['Authorization'] = `Bearer ${token}`;
//   }

//   return req.clone({
//     setHeaders: headers,
//   });
// }

function refreshAndRetry(
  http: HttpClient,
  router: Router,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = localStorage.getItem(LocalStorageKey.refreshToken);

    if (!refreshToken) {
      isRefreshing = false;
      clearSessionAndRedirect(router);
      return throwError(() => new Error('No refresh token'));
    }

    return http
      .post<ApiResponse<RefreshTokenResult>>(
        `${AppConst.apiBaseUrl}/api/TokenAuth/RefreshToken`,
        null,
        { params: { refreshToken } }
      )
      .pipe(
        switchMap((res) => {
          isRefreshing = false;
          const newToken = res.result.accessToken;
          localStorage.setItem(LocalStorageKey.token, newToken);
          refreshTokenSubject.next(newToken);
          return next(withAuthHeader(req, newToken));
        }),
        catchError((err) => {
          isRefreshing = false;
          clearSessionAndRedirect(router);
          return throwError(() => err);
        })
      );
  }

  // Another request is already refreshing — wait for the new token
  return refreshTokenSubject.pipe(
    filter((token) => token !== null),
    take(1),
    switchMap((token) => next(withAuthHeader(req, token)))
  );
}

function clearSessionAndRedirect(router: Router) {
  localStorage.removeItem(LocalStorageKey.token);
  localStorage.removeItem(LocalStorageKey.refreshToken);
  localStorage.removeItem(LocalStorageKey.userId);
  localStorage.removeItem(LocalStorageKey.userPortals);
  // Keep the user in whatever portal they're currently in.
  router.navigate([resolveLoginRoute(router.url)]);
}
